/**
 * On-canvas bootstrap log for device debugging (especially iPad black-screen triage).
 * Shown automatically on tablets; force with ?diag=1, hide with ?diag=0.
 */
export class ViewerDiagnostics {
  private readonly wrap: HTMLDivElement;
  private readonly el: HTMLDivElement;
  private readonly copyBtn: HTMLButtonElement;
  /** Full session log — never truncated; used by Copy log. */
  private readonly allLines: string[] = [];
  /** On-screen rolling window only. */
  private readonly maxDisplayLines = 24;

  constructor(parent: HTMLElement, enabled: boolean) {
    this.wrap = document.createElement('div');
    this.wrap.className = 'viewer-diagnostics-wrap';
    if (!enabled) this.wrap.hidden = true;

    this.el = document.createElement('div');
    this.el.className = 'viewer-diagnostics';
    this.el.setAttribute('aria-live', 'polite');

    this.copyBtn = document.createElement('button');
    this.copyBtn.type = 'button';
    this.copyBtn.className = 'viewer-diagnostics-copy';
    this.copyBtn.textContent = 'Copy log';
    this.copyBtn.addEventListener('click', () => {
      void this.copyToClipboard();
    });

    this.wrap.appendChild(this.el);
    this.wrap.appendChild(this.copyBtn);
    parent.appendChild(this.wrap);
    this.refreshDisplay();
  }

  get enabled(): boolean {
    return !this.wrap.hidden;
  }

  log(message: string): void {
    const line = `+${performance.now().toFixed(0)}ms ${message}`;
    this.allLines.push(line);
    this.refreshDisplay();
    console.info('[viewer-diag]', message);
  }

  /** Recent lines for HUD sync; full history via copyLogText(). */
  getLines(): readonly string[] {
    return this.allLines;
  }

  copyLogText(): string {
    const header = [
      `build: ${readBuildId()}`,
      `url: ${location.href}`,
      `ua: ${navigator.userAgent}`,
      `lines: ${this.allLines.length}`,
      '---',
    ];
    return [...header, ...this.allLines].join('\n');
  }

  private refreshDisplay(): void {
    const start = Math.max(0, this.allLines.length - this.maxDisplayLines);
    const visible = this.allLines.slice(start);
    if (this.allLines.length > this.maxDisplayLines) {
      visible.unshift(`… ${this.allLines.length - this.maxDisplayLines} earlier lines (tap Copy log for all) …`);
    }
    this.el.textContent = visible.join('\n');
    this.copyBtn.textContent =
      this.allLines.length > 0 ? `Copy log (${this.allLines.length})` : 'Copy log';
  }

  private async copyToClipboard(): Promise<void> {
    const text = this.copyLogText();
    const markCopied = () => {
      this.copyBtn.textContent = `Copied ${this.allLines.length}!`;
      window.setTimeout(() => this.refreshDisplay(), 2000);
    };
    const markFailed = () => {
      this.copyBtn.textContent = 'Copy failed — select text below';
      window.setTimeout(() => this.refreshDisplay(), 3000);
      // Last resort on iPad: make log selectable so the user can long-press → Copy.
      this.el.style.pointerEvents = 'auto';
      this.el.style.userSelect = 'text';
      this.el.style.webkitUserSelect = 'text';
    };

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        markCopied();
        return;
      }
    } catch {
      // fall through to execCommand
    }

    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    // iOS Safari often rejects off-screen textarea copy — keep it in the viewport.
    ta.style.cssText =
      'position:fixed;top:0;left:0;width:2px;height:2px;padding:0;border:0;opacity:0.01';
    document.body.appendChild(ta);
    ta.focus({ preventScroll: true });
    ta.select();
    ta.setSelectionRange(0, text.length);
    try {
      const ok = document.execCommand('copy');
      if (ok) markCopied();
      else markFailed();
    } catch {
      markFailed();
    } finally {
      ta.remove();
    }
  }

  dispose(): void {
    this.wrap.remove();
  }
}

export function isDiagnosticsEnabled(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.get('diag') === '0') return false;
  return params.has('diag');
}

/** iPadOS often reports as Macintosh — needed for tablet-specific guards. */
export function detectTablet(): boolean {
  if (/iPad/i.test(navigator.userAgent)) return true;
  return /Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
}

export function readBuildId(): string {
  const src = document.querySelector('script[src*="/assets/main-"]')?.getAttribute('src') ?? '';
  return src.match(/main-([^.]+)/)?.[1] ?? '?';
}
